import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, BadgePercent } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { rp, num, parseNum, todayWIB } from "../lib/format";
import { PageWrap, Empty } from "../components/common";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";

const EMPTY = { name: "", type: "percent", value: 10, scope: "all", target_ids: [], min_qty: 1, start_date: "", end_date: "", active: true };
const today = todayWIB();
const status = (p) => (!p.active ? ["Nonaktif", "secondary"] : p.end_date && p.end_date < today ? ["Berakhir", "secondary"] : p.start_date && p.start_date > today ? ["Terjadwal", "outline"] : ["Berjalan", "default"]);

const PromoForm = ({ form, setForm, products, cats, onSaved }) => {
  const [q, setQ] = useState("");
  if (!form) return null;
  const targets = form.scope === "category" ? cats : products.filter((p) => !q || p.name.toLowerCase().includes(q.toLowerCase()));
  const toggle = (id, v) => setForm({ ...form, target_ids: v ? [...form.target_ids, id] : form.target_ids.filter((x) => x !== id) });
  const save = async (e) => {
    e.preventDefault();
    try {
      const body = { ...form, value: Number(form.value), min_qty: Number(form.min_qty) || 1, target_ids: form.scope === "all" ? [] : form.target_ids };
      delete body.id; delete body.store_id; delete body.created_at;
      if (form.id) await api.put(`/promotions/${form.id}`, body);
      else await api.post("/promotions", body);
      toast.success("Promo disimpan");
      setForm(null);
      onSaved();
    } catch (e2) {
      toast.error(errMsg(e2));
    }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && setForm(null)}>
      <DialogContent className="max-w-lg max-h-[95vh] overflow-y-auto" data-testid="promo-form-dialog">
        <DialogHeader><DialogTitle className="font-display">{form.id ? "Edit" : "Buat"} Promo / Diskon</DialogTitle><DialogDescription>Diskon diterapkan otomatis di kasir sesuai aturan.</DialogDescription></DialogHeader>
        <form onSubmit={save} className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1.5"><Label>Nama Promo</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="cth: Diskon Akhir Pekan" data-testid="promo-name-input" /></div>
          <div className="space-y-1.5"><Label>Jenis</Label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger data-testid="promo-type-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="percent">Persen (%)</SelectItem><SelectItem value="nominal">Nominal (Rp / item)</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Nilai</Label><Input required inputMode="numeric" className="font-mono" value={form.type === "nominal" ? num(form.value) : form.value} onChange={(e) => setForm({ ...form, value: parseNum(e.target.value) })} data-testid="promo-value-input" /></div>
          <div className="space-y-1.5"><Label>Berlaku untuk</Label>
            <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v, target_ids: [] })}>
              <SelectTrigger data-testid="promo-scope-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Semua produk</SelectItem><SelectItem value="category">Kategori tertentu</SelectItem><SelectItem value="product">Produk tertentu</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Min. Qty per item</Label><Input inputMode="numeric" value={form.min_qty} onChange={(e) => setForm({ ...form, min_qty: parseNum(e.target.value) || 1 })} data-testid="promo-minqty-input" /></div>
          {form.scope !== "all" && (
            <div className="col-span-2 space-y-2">
              {form.scope === "product" && <Input placeholder="Cari produk..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="promo-target-search" />}
              <div className="max-h-44 overflow-y-auto thin-scroll rounded-lg border divide-y">
                {targets.map((t) => (
                  <label key={t.id} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-accent">
                    <Checkbox checked={form.target_ids.includes(t.id)} onCheckedChange={(v) => toggle(t.id, !!v)} data-testid={`promo-target-${t.id}`} />{t.name}
                  </label>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">{form.target_ids.length} dipilih</p>
            </div>
          )}
          <div className="space-y-1.5"><Label>Mulai</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="promo-start-input" /></div>
          <div className="space-y-1.5"><Label>Berakhir</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="promo-end-input" /></div>
          <label className="col-span-2 flex items-center justify-between text-sm"><span>Aktif</span><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="promo-active-switch" /></label>
          <Button type="submit" className="col-span-2" data-testid="promo-save-btn">Simpan Promo</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function Promotions() {
  const [rows, setRows] = useState([]);
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState(null);
  const [del, setDel] = useState(null);
  const load = useCallback(() => api.get("/promotions").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); api.get("/products").then((r) => setProducts(r.data)); api.get("/categories").then((r) => setCats(r.data)); }, [load]);
  const names = Object.fromEntries([...products, ...cats].map((x) => [x.id, x.name]));
  const remove = async () => { await api.delete(`/promotions/${del.id}`); toast.success("Promo dihapus"); setDel(null); load(); };
  return (
    <PageWrap>
      <div className="flex justify-between items-center gap-3">
        <p className="text-sm text-muted-foreground">Atur diskon produk, kategori, atau seluruh toko. Diskon terbaik otomatis dipakai di kasir.</p>
        <Button onClick={() => setForm({ ...EMPTY })} data-testid="add-promo-btn"><Plus className="h-4 w-4 mr-2" />Buat Promo</Button>
      </div>
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {rows.map((p) => {
          const [s, v] = status(p);
          return (
            <div key={p.id} className="rounded-2xl border bg-card p-5 space-y-3 fade-up" data-testid={`promo-card-${p.id}`}>
              <div className="flex items-start gap-3">
                <div className="h-11 w-11 rounded-xl bg-primary/15 text-primary grid place-items-center shrink-0"><BadgePercent className="h-5 w-5" /></div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold truncate">{p.name}</div>
                  <div className="font-mono font-bold text-primary text-lg">{p.type === "percent" ? `${p.value}%` : `${rp(p.value)}/item`}</div>
                </div>
                <Badge variant={v}>{s}</Badge>
              </div>
              <div className="text-xs text-muted-foreground space-y-1">
                <div>Berlaku: {p.scope === "all" ? "Semua produk" : p.target_ids.map((id) => names[id] || "?").join(", ")}</div>
                {p.min_qty > 1 && <div>Minimal beli {p.min_qty} pcs</div>}
                <div>Periode: {p.start_date || "sekarang"} s/d {p.end_date || "seterusnya"}</div>
              </div>
              <div className="flex gap-1 justify-end">
                <Button size="sm" variant="ghost" onClick={() => setForm(p)} data-testid={`edit-promo-${p.id}`}><Pencil className="h-4 w-4 mr-1" />Edit</Button>
                <Button size="sm" variant="ghost" className="text-destructive" onClick={() => setDel(p)} data-testid={`delete-promo-${p.id}`}><Trash2 className="h-4 w-4 mr-1" />Hapus</Button>
              </div>
            </div>
          );
        })}
      </div>
      {!rows.length && <Empty icon={BadgePercent} text="Belum ada promo. Buat promo pertama Anda." />}
      <PromoForm form={form} setForm={setForm} products={products} cats={cats} onSaved={load} />
      <ConfirmDialog open={!!del} onOpenChange={(o) => !o && setDel(null)} title="Hapus promo?" description={`Promo "${del?.name}" akan dihapus.`} onConfirm={remove} />
    </PageWrap>
  );
}
